/**
 * Where a plan’s loose recommendations and notes belong.
 *
 * A recommendation attached to no plan item is either about one section of the plan or about the
 * plan as a whole. The extractor writes the section it means into the recommendation’s `section`,
 * its placement; a plan honors that placement when it hosts the section, that is, when it has items
 * there for the section’s page to show. Everything else—`general`, a section the plan lacks, a
 * value this app does not know—is placed with the general recommendations. A note carries a
 * placement of its own when it was added on a section’s page; otherwise it follows the
 * recommendation it was created from.
 *
 * This module is the one partition of the loose targets that the tallies, the rundowns, the
 * continuation, the general page, and the section pages all read, so that “general” means the
 * same remainder everywhere.
 */
import { PLAN_TYPE_504, PLAN_TYPE_IEP, PLAN_TYPE_IPP } from '../arch/iepConstants'

import { PLACEABLE_SECTION_KEYS, PLACEMENT_GENERAL, SECTION_KEYS_BY_PLAN_TYPE } from './constants'

/**
 * Whether an IEP/504 recommendation or concern is attached to no plan item: its association arrays
 * (goals, services, accommodations, PLOPs) are all empty. Modifications associate through the
 * accommodations array, since the back end holds them as accommodations.
 *
 * @param {object} target the recommendation or concern; any array that is absent counts as empty
 * @returns whether every association array is empty
 */
const unattachedFilter = ({ goals = [], services = [], accommodations = [], plops = [] }) =>
  goals.length === 0 && services.length === 0 && accommodations.length === 0 && plops.length === 0

/**
 * IPP counterpart of `unattachedFilter`, over the IPP’s association arrays.
 *
 * @param {object} target the recommendation or concern; any array that is absent counts as empty
 * @returns whether every association array is empty
 */
const ippUnattachedFilter = ({ priorities = [], outcomes = [], goals = [], services = [], supports = [] }) =>
  priorities.length === 0 &&
  outcomes.length === 0 &&
  goals.length === 0 &&
  services.length === 0 &&
  supports.length === 0

const UNATTACHED_FILTERS = {
  [PLAN_TYPE_IEP]: unattachedFilter,
  [PLAN_TYPE_504]: unattachedFilter,
  [PLAN_TYPE_IPP]: ippUnattachedFilter
}

const NO_TARGETS = []
const NO_SECTION_KEYS = []

/**
 * Reads a recommendation’s placement: the section key it names, when that is one this app has a
 * page for, and the general placement otherwise. The match is exact, since the extractor writes the
 * canonical key; `general`, `abilities` (no page yet), an absent value, and anything a newer
 * producer might emit all read as general rather than as lost.
 *
 * @param {object} recommendation the recommendation whose placement is wanted
 * @returns a section key from PLACEABLE_SECTION_KEYS, or PLACEMENT_GENERAL
 */
const getRecommendationPlacement = recommendation => {
  const { section } = recommendation ?? {}
  return PLACEABLE_SECTION_KEYS.includes(section) ? section : PLACEMENT_GENERAL
}

/**
 * Determines which sections a plan hosts: those of its plan type’s sections that have items, so
 * that a page exists to show a placement there.
 *
 * @param {object} plan the plan
 * @returns a Set of the hosted section keys
 */
const getSectionHosts = plan => {
  const { planType } = plan ?? {}
  const sectionKeys = SECTION_KEYS_BY_PLAN_TYPE[planType] ?? NO_SECTION_KEYS

  return new Set(sectionKeys.filter(sectionKey => (plan[sectionKey]?.length ?? 0) > 0))
}

/**
 * Partitions a plan’s loose recommendations and concerns by where they are placed: one group per
 * hosted section, and the general remainder. A recommendation is placed in its section when the
 * plan hosts it, else in general; a concern is placed with the loose recommendation it was created
 * from, else in general. Attached targets are left out entirely: they belong to their items.
 *
 * Nothing here is filtered by visibility (dismissed recommendations, resolved notes); that is the
 * caller’s concern, as is ordering.
 *
 * @param {object} plan the plan whose loose targets to partition
 * @returns `{ sectionRecommendations, sectionConcerns, generalRecommendations, generalConcerns }`,
 *   the first two keyed by the plan type’s section keys (every key present, empty or not); all
 *   empty for an unrecognized plan type
 */
const partitionLooseTargets = plan => {
  const { planType, recommendations = NO_TARGETS, concerns = NO_TARGETS, recommendationsMap = {} } = plan ?? {}

  const unattached = UNATTACHED_FILTERS[planType]
  const sectionKeys = SECTION_KEYS_BY_PLAN_TYPE[planType] ?? NO_SECTION_KEYS

  const sectionRecommendations = Object.fromEntries(sectionKeys.map(sectionKey => [sectionKey, []]))
  const sectionConcerns = Object.fromEntries(sectionKeys.map(sectionKey => [sectionKey, []]))
  const generalRecommendations = []
  const generalConcerns = []

  if (!unattached) {
    return { sectionRecommendations, sectionConcerns, generalRecommendations, generalConcerns }
  }

  const hosts = getSectionHosts(plan)

  const hostOf = recommendation => {
    const placement = getRecommendationPlacement(recommendation)
    return hosts.has(placement) ? placement : undefined
  }

  recommendations.filter(unattached).forEach(recommendation => {
    const host = hostOf(recommendation)
    ;(host ? sectionRecommendations[host] : generalRecommendations).push(recommendation)
  })

  concerns.filter(unattached).forEach(concern => {
    const source = concern.createdFrom ? recommendationsMap[concern.createdFrom] : undefined
    const host = hostOf(concern) ?? (source && unattached(source) ? hostOf(source) : undefined)
    ;(host ? sectionConcerns[host] : generalConcerns).push(concern)
  })

  return { sectionRecommendations, sectionConcerns, generalRecommendations, generalConcerns }
}

export {
  UNATTACHED_FILTERS,
  getRecommendationPlacement,
  getSectionHosts,
  ippUnattachedFilter,
  partitionLooseTargets,
  unattachedFilter
}
